import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";

export type GatewayCredentialProtocol = "SSH" | "RDP";
export type GatewayCredentialAuthType = "Key" | "Password";

export interface StoreGatewayCredentialPayload {
  label: string;
  username: string;
  password: string;
  retypePassword: string;
  tenantName: string;
  os: string;
  computerId: string;
  createdBy: string;
  userId: string;
  fromGateway: boolean;
  protocol: GatewayCredentialProtocol;
  protocolAuth: GatewayCredentialAuthType;
  privateKey: string;
  passPharse: string;
}


export const publishCredentials = async (tenantName: string, credentialId: string, roleList: string[]) => {
  const url = `${NGROK}/api/credentials/publish?tenantName=${tenantName}&credentialId=${credentialId}&rolesList=${roleList}`;
  const data = await apiClient({
    method: "PUT",
    url,
  });
  return data;
}

export const getCredentialsList = async (tenantName: string, deviceId: string) => {
  const url = `${NGROK}/api/${tenantName}/credentials?computerId=${deviceId}`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const fetchCredentialByLabel = async (label: string) => {
  const encodedLabel = encodeURIComponent(label);
  const url = `${NGROK}/api/credentials/${encodedLabel}`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const getGatewayCredentialsList = async (tenantName: string, deviceId: string) => {
  const url = `${NGROK}/api/${tenantName}/credentials?computerId=${deviceId}&isFromGateway=true`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const getUsersList = async (tenantName: string, deviceId: string) => {
  const url = `${NGROK}/api/${tenantName}/computer-users/${deviceId}`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};


export const deleteCredential = async ({ label, deviceId }: { label: string, deviceId: string }) => {
  const url = `${NGROK}/api/credentials/${label}?computerId=${deviceId}`;
  return await apiClient({
    method: "DELETE",
    url,
  });
};


export const createNewCredentials = async ({
  label,
  username,
  password,
  autoRotate,
  userId,
  tenantName,
  email,
  deviceId
}: {
  label: string;
  username: string;
  password: string;
  autoRotate: boolean;
  userId: string;
  tenantName: string;
  email: string;
  deviceId: string;
}) => {
  const url = `${NGROK}/api/credentials`;
  return await apiClient({
    method: "POST",
    url,
    data: {
      label,
      username,
      password,
      retypePassword: password,
      tenantName,
      os: "WINDOWS",
      computerId: deviceId,
      createdBy: email,
      userId,
      fromGateway: false,
      isFromGateway: false,
      autoRotate
    }
  });
};

export const updateCredentialAutoRotation = async ({
  computerId,
  label,
  enabled,
}: {
  computerId: string;
  label: string;
  enabled: boolean;
}) => {
  const url = `${NGROK}/api/credentials/auto-rotation`;
  return await apiClient({
    method: "PUT",
    url,
    data: {
      computerId,
      label,
      enabled,
      autoRotate: enabled
    },
  });
};

export const rotateCredentialNow = async ({
  computerId,
  label,
  username,
}: {
  computerId: string;
  label: string;
  username: string;
}) => {
  const url = `${NGROK}/api/credentials/rotate`;
  return await apiClient({
    method: "POST",
    url,
    data: {
      computerId,
      label,
      username,
    },
  });
};

export const storeGatewayCredential = async (payload: StoreGatewayCredentialPayload) => {
  const url = `${NGROK}/api/credentials`;
  return await apiClient({
    method: "POST",
    url,
    data: payload,
  });
};

export const revealCredentialPassword = async ({
  computerId,
  tenantName,
  label,
}: {
  computerId: string;
  tenantName: string;
  label: string;
}): Promise<string> => {
  const url = `${NGROK}/api/credentials/reveal?computerId=${encodeURIComponent(computerId)}&tenantName=${encodeURIComponent(tenantName)}&label=${encodeURIComponent(label)}`;
  const data = await apiClient({
    method: "GET",
    url,
  });
  return typeof data === "string" ? data : String(data?.password ?? data ?? "");
};


